import { Button, Flex, Grid, HStack, Heading, Tooltip, VStack } from "@chakra-ui/react";
import { FunctionComponent, ReactElement } from "react";
import { FaFileInvoiceDollar, FaMoneyCheckAlt, FaChartBar, FaUsers, FaUser } from "react-icons/fa";
import { HiBriefcase, HiChevronDoubleLeft, HiChevronDoubleRight, HiHome, HiFolder } from "react-icons/hi";

// Components
import NavItem from "./NavItem";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import { UserRole } from "../../../utils/consts";

interface SidebarProps {
	sidebarOpen: boolean;
	setSidebarOpen: (value: boolean) => void;
}

/**
 * Handles the Sidebar displayed to the side of the common layout
 */
const Sidebar: FunctionComponent<SidebarProps> = (props: SidebarProps): ReactElement => {
	// Props
	const { sidebarOpen, setSidebarOpen } = props;
	const { hasRole } = useAuth();

	const isAdmin = hasRole([
		UserRole.Admin,
		UserRole.ClaimsAdmin,
		UserRole.ClaimsAnalyst,
		UserRole.ClaimsManager,
		UserRole.ClaimsViewOnly,
		UserRole.ClaimsNetworkManager,
		UserRole.Payments
	]);
	const isClaimsManager = hasRole([UserRole.ClaimsManager]);
	const isAttorney = hasRole([UserRole.Provider]);

	/**
	 * Handles toggling the Sidebar opened or closed
	 */
	const toggleSidebar = () => {
		setSidebarOpen(!sidebarOpen);
	};

	return (
		<VStack height={"100vh"} bg="white" display={"flex"} boxShadow="2px 5px 7px 3px rgb(0 0 0 / 5%)" zIndex="1">
			<Grid>
				<Flex alignItems={"flex-start"} flexDirection={"column"}>
					<Tooltip hasArrow label="Expand" bg="gray.200" w="100%" color="black" isDisabled={sidebarOpen}>
						<Button marginTop={"10px"} bg="none" w="100%" borderRadius="0px" onClick={toggleSidebar}>
							<HStack fontSize={"30px"} alignItems={"flex-end"} w="100%" color="orange.500">
								{!sidebarOpen && <HiChevronDoubleRight fontSize="25px" />}
								{sidebarOpen && (
									<>
										<HiChevronDoubleLeft fontSize="25px" />
										<Flex justifyContent={"space-between"} alignItems={"center"}>
											<Heading as="h4" fontSize="20px" fontWeight="500" color="gray.700" pb="1px">
												Collapse
											</Heading>
										</Flex>
									</>
								)}
							</HStack>
						</Button>
					</Tooltip>
					{isAdmin && (
						<>
							<NavItem
								label="Claims"
								icon={<FaFileInvoiceDollar fontSize="25px" />}
								path={"/claims"}
								sidebarOpen={sidebarOpen}
								firstElement
							/>
							<NavItem label="Users" icon={<FaUsers fontSize="25px" />} path={"/users"} sidebarOpen={sidebarOpen} />
							<NavItem
								label="Payments"
								icon={<FaMoneyCheckAlt fontSize="25px" />}
								path={"/payments"}
								sidebarOpen={sidebarOpen}
							/>
							{isClaimsManager && (
								<NavItem
									label="Reports"
									icon={<FaChartBar fontSize="25px" />}
									path={"/reports"}
									sidebarOpen={sidebarOpen}
								/>
							)}
						</>
					)}
					{isAttorney && (
						<>
							<NavItem
								label="Claims"
								icon={<FaFileInvoiceDollar fontSize="25px" />}
								path={"/attorney/claims"}
								sidebarOpen={sidebarOpen}
								firstElement
							/>
							<NavItem
								label="Cases"
								icon={<HiFolder fontSize="25px" />}
								path={"/attorney/cases"}
								sidebarOpen={sidebarOpen}
							/>
							<NavItem
								label="Profile"
								icon={<FaUser fontSize="25px" />}
								path={"/attorney/profile"}
								sidebarOpen={sidebarOpen}
							/>
						</>
					)}
				</Flex>
			</Grid>
		</VStack>
	);
};

export default Sidebar;
